// ============================================
// moldClaw - Payment Failed Page (Polar)
// ============================================

/**
 * 🧑‍💻 SWE-2: 결제 실패 페이지
 * 🎨 CX-2: 한국 SaaS 결제 실패 UX 벤치마킹
 *   - 토스: 실패 시 "다시 시도" 버튼이 바로 보임 ✅
 *   - 명확한 실패 사유 표시
 *   - 대체 결제 수단 안내
 * 🔧 POLAR: Polar Checkout 실패 처리 (2026-03-16)
 */

import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { usePayment } from '../hooks/usePayment';

// 실패 코드별 메시지 (Polar 에러 코드 포함)
const FAILURE_MESSAGES: Record<string, { title: string; description: string; suggestion: string }> = {
  // 사용자 취소
  USER_CANCEL: {
    title: '결제 취소됨',
    description: '결제를 취소하셨습니다.',
    suggestion: '결제를 다시 시도하시려면 아래 버튼을 눌러주세요.',
  },
  
  // 카드 관련 에러
  CARD_DECLINED: {
    title: '카드 거절됨',
    description: '카드사에서 결제를 거절했습니다.',
    suggestion: '카드사에 문의하시거나 다른 카드를 사용해보세요.',
  },
  CARD_EXPIRED: {
    title: '카드 유효기간 만료',
    description: '카드의 유효기간이 만료되었습니다.',
    suggestion: '유효한 카드로 다시 시도해주세요.',
  },
  INSUFFICIENT_FUNDS: {
    title: '잔액 부족',
    description: '카드 또는 계좌 잔액이 부족합니다.',
    suggestion: '잔액을 확인하시거나 다른 결제 수단을 사용해보세요.',
  },
  
  // Polar 특정 에러
  CHECKOUT_EXPIRED: {
    title: '결제 세션 만료',
    description: '결제 시간이 초과되었습니다.',
    suggestion: '요금제 페이지에서 다시 결제를 진행해주세요.',
  },
  PAYMENT_FAILED: {
    title: '결제 처리 실패',
    description: '결제 처리 중 오류가 발생했습니다.',
    suggestion: '잠시 후 다시 시도하시거나 다른 결제 수단을 사용해보세요.',
  },
  SUBSCRIPTION_EXISTS: {
    title: '이미 구독 중',
    description: '이미 활성화된 구독이 있습니다.',
    suggestion: '대시보드에서 현재 구독을 확인해주세요.',
  },
  
  // 네트워크/기술적 에러
  NETWORK_ERROR: {
    title: '네트워크 오류',
    description: '결제 처리 중 네트워크 문제가 발생했습니다.',
    suggestion: '인터넷 연결을 확인하고 다시 시도해주세요.',
  },
  
  // 기본 에러
  DEFAULT: {
    title: '결제 실패',
    description: '결제 처리 중 문제가 발생했습니다.',
    suggestion: '잠시 후 다시 시도하시거나 다른 결제 수단을 사용해보세요.',
  },
};

export default function PaymentFailed() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { checkout, isLoading, error: paymentError, clearError } = usePayment();
  
  // URL 파라미터에서 실패 정보 추출
  const errorCode = searchParams.get('code') || 'DEFAULT';
  const errorMessage = searchParams.get('message');
  const tier = searchParams.get('tier') as 'pro' | 'team' | null;
  const checkoutId = searchParams.get('checkout_id');
  
  const failureInfo = FAILURE_MESSAGES[errorCode] || FAILURE_MESSAGES.DEFAULT;
  
  // 다시 시도 핸들러
  const handleRetry = async () => {
    clearError();
    
    if (tier) {
      try {
        await checkout(tier);
      } catch {
        // 에러는 usePayment에서 처리됨
      }
    } else {
      navigate('/pricing');
    }
  };
  
  return (
    <div className="min-h-screen bg-gray-900 flex items-center justify-center px-4">
      <div className="max-w-md w-full">
        {/* Failed Card */}
        <div className="bg-gray-800 rounded-2xl p-8 text-center shadow-xl">
          {/* Failed Icon */}
          <div className="w-20 h-20 mx-auto mb-6 bg-red-500/20 rounded-full flex items-center justify-center">
            <svg
              className="w-10 h-10 text-red-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </div>
          
          {/* Title */}
          <h1 className="text-2xl font-bold text-white mb-2">
            {failureInfo.title}
          </h1>
          
          <p className="text-gray-400 mb-2">
            {failureInfo.description}
          </p>
          
          {/* Custom Error Message (if provided) */}
          {errorMessage && errorMessage !== failureInfo.description && (
            <p className="text-sm text-red-400 mb-4 bg-red-500/10 rounded-lg p-2">
              상세: {errorMessage}
            </p>
          )}
          
          {/* Payment Error (from hook) */}
          {paymentError && (
            <p className="text-sm text-red-400 mb-4 bg-red-500/10 rounded-lg p-2">
              {paymentError}
            </p>
          )}
          
          {/* Suggestion */}
          <div className="bg-gray-700/50 rounded-xl p-4 mb-6 text-left">
            <p className="text-sm text-gray-300">
              💡 {failureInfo.suggestion}
            </p>
          </div>
          
          {/* Actions */}
          <div className="space-y-3">
            {/* Primary: 다시 시도 */}
            <button
              onClick={handleRetry}
              disabled={isLoading}
              className="w-full py-3 px-4 bg-space-blue hover:brightness-110 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg font-medium transition-all flex items-center justify-center gap-2"
            >
              {isLoading ? (
                <>
                  <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" 
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  <span>처리 중...</span>
                </>
              ) : (
                <>
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                      d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  <span>다시 시도</span>
                </>
              )}
            </button>
            
            {/* 요금제 페이지로 이동 */}
            <button
              onClick={() => navigate('/pricing')}
              className="w-full py-3 px-4 bg-gray-700 hover:bg-gray-600 text-white rounded-lg font-medium transition-colors"
            >
              요금제 페이지로 이동
            </button>
            
            {/* Back to Dashboard */}
            <Link
              to="/dashboard"
              className="block text-sm text-gray-500 hover:text-gray-400 transition-colors mt-4"
            >
              대시보드로 돌아가기
            </Link>
          </div>
        </div>
        
        {/* Help Options */}
        <div className="mt-6 bg-gray-800/50 rounded-xl p-4">
          <p className="text-gray-400 text-sm mb-3">계속 문제가 발생하나요?</p>
          <div className="flex flex-col sm:flex-row gap-2 text-sm">
            <a
              href="mailto:support@moldclaw.com"
              className="text-space-blue-light hover:text-white transition-colors"
            >
              📧 이메일 문의
            </a>
            <span className="hidden sm:inline text-gray-600">|</span>
            <Link
              to="/help/payment"
              className="text-space-blue-light hover:text-white transition-colors"
            >
              📖 결제 FAQ
            </Link>
          </div>
        </div>
        
        {/* Error Code (for debugging) */}
        <div className="text-center text-gray-600 text-xs mt-4 space-y-1">
          <p>오류 코드: {errorCode}</p>
          {checkoutId && <p>Checkout ID: {checkoutId}</p>}
        </div>
      </div>
    </div>
  );
}
